import { LedgerNormalBalanceEnum, LedgerTagKindEnum, Tag } from '../../../cfo/models/ledger.models';
import { ID } from '../../../models/id';

interface MockEntryLine {
  accountId: ID;
  side: LedgerNormalBalanceEnum;
  cents: number;
}

interface MockEntry {
  id: ID;
  number: string;
  day: number;
  memo: string;
  sourceType: string;
  sourceLabel: string;
  tags: Tag[];
  lines: MockEntryLine[];
}

interface AccountActivity {
  debitCents: number;
  creditCents: number;
}

const { DEBIT, CREDIT } = LedgerNormalBalanceEnum;

const RIVERSIDE_JOB: Tag = { kind: LedgerTagKindEnum.JOB, id: 1, name: 'Riverside Medical Center' };
const OAK_STREET_JOB: Tag = { kind: LedgerTagKindEnum.JOB, id: 2, name: 'Oak Street Remodel' };
const APEX_VENDOR: Tag = { kind: LedgerTagKindEnum.VENDOR, id: 1, name: 'Apex Electrical' };
const LUMBER_VENDOR: Tag = { kind: LedgerTagKindEnum.VENDOR, id: 2, name: 'Summit Lumber Supply' };
const INSURANCE_VENDOR: Tag = { kind: LedgerTagKindEnum.VENDOR, id: 3, name: 'Harbor Insurance Group' };
const RIVERSIDE_CUSTOMER: Tag = { kind: LedgerTagKindEnum.CUSTOMER, id: 1, name: 'Riverside Health' };
const GARCIA_CUSTOMER: Tag = { kind: LedgerTagKindEnum.CUSTOMER, id: 2, name: 'Garcia Family' };

const mockEntries: MockEntry[] = [
  {
    id: 101,
    number: 'JE-0101',
    day: 3,
    memo: 'Progress billing 3, Riverside Medical Center',
    sourceType: 'invoice',
    sourceLabel: 'Invoice 1042',
    tags: [RIVERSIDE_JOB, RIVERSIDE_CUSTOMER],
    lines: [
      { accountId: 4, side: DEBIT, cents: 4_800_000 },
      { accountId: 18, side: CREDIT, cents: 4_800_000 },
    ],
  },
  {
    id: 102,
    number: 'JE-0102',
    day: 5,
    memo: 'Payment received, Riverside Medical Center',
    sourceType: 'payment',
    sourceLabel: 'Payment 8831',
    tags: [RIVERSIDE_JOB, RIVERSIDE_CUSTOMER],
    lines: [
      { accountId: 2, side: DEBIT, cents: 3_000_000 },
      { accountId: 4, side: CREDIT, cents: 3_000_000 },
    ],
  },
  {
    id: 103,
    number: 'JE-0103',
    day: 8,
    memo: 'Electrical rough-in, Apex Electrical',
    sourceType: 'bill',
    sourceLabel: 'Bill A-2210',
    tags: [RIVERSIDE_JOB, APEX_VENDOR],
    lines: [
      { accountId: 22, side: DEBIT, cents: 1_250_000 },
      { accountId: 9, side: CREDIT, cents: 1_250_000 },
    ],
  },
  {
    id: 104,
    number: 'JE-0104',
    day: 10,
    memo: 'Framing lumber, Summit Lumber Supply',
    sourceType: 'bill',
    sourceLabel: 'Bill S-5518',
    tags: [OAK_STREET_JOB, LUMBER_VENDOR],
    lines: [
      { accountId: 21, side: DEBIT, cents: 820_000 },
      { accountId: 9, side: CREDIT, cents: 820_000 },
    ],
  },
  {
    id: 105,
    number: 'JE-0105',
    day: 14,
    memo: 'Vendor payment, Apex Electrical',
    sourceType: 'bill_payment',
    sourceLabel: 'Check 2057',
    tags: [APEX_VENDOR],
    lines: [
      { accountId: 9, side: DEBIT, cents: 1_500_000 },
      { accountId: 2, side: CREDIT, cents: 1_500_000 },
    ],
  },
  {
    id: 106,
    number: 'JE-0106',
    day: 15,
    memo: 'Payroll run, field crew',
    sourceType: 'manual',
    sourceLabel: 'Payroll run',
    tags: [],
    lines: [
      { accountId: 20, side: DEBIT, cents: 2_200_000 },
      { accountId: 2, side: CREDIT, cents: 1_750_000 },
      { accountId: 11, side: CREDIT, cents: 450_000 },
    ],
  },
  {
    id: 107,
    number: 'JE-0107',
    day: 18,
    memo: 'Deposit received, Oak Street Remodel',
    sourceType: 'payment',
    sourceLabel: 'Payment 8847',
    tags: [OAK_STREET_JOB, GARCIA_CUSTOMER],
    lines: [
      { accountId: 2, side: DEBIT, cents: 900_000 },
      { accountId: 19, side: CREDIT, cents: 900_000 },
    ],
  },
  {
    id: 108,
    number: 'JE-0108',
    day: 22,
    memo: 'General liability premium',
    sourceType: 'bill',
    sourceLabel: 'Bill H-0912',
    tags: [INSURANCE_VENDOR],
    lines: [
      { accountId: 24, side: DEBIT, cents: 180_000 },
      { accountId: 2, side: CREDIT, cents: 180_000 },
    ],
  },
  {
    id: 109,
    number: 'JE-0109',
    day: 25,
    memo: 'Equipment loan payment',
    sourceType: 'manual',
    sourceLabel: 'Loan payment',
    tags: [],
    lines: [
      { accountId: 13, side: DEBIT, cents: 240_000 },
      { accountId: 2, side: CREDIT, cents: 240_000 },
    ],
  },
];

export function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function getAccountActivity(accountId: ID): AccountActivity {
  return mockEntries
    .flatMap(({ lines }) => lines)
    .filter((line) => line.accountId === accountId)
    .reduce<AccountActivity>(
      (activity, { side, cents }) =>
        side === DEBIT
          ? { ...activity, debitCents: activity.debitCents + cents }
          : { ...activity, creditCents: activity.creditCents + cents },
      { debitCents: 0, creditCents: 0 },
    );
}
